import React from 'react';
import { faBookReader, faShapes } from '@fortawesome/free-solid-svg-icons';
import { Box, Paper, Typography, Skeleton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  remCalc,
  translate,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import theme, { FONT_GSS_FUTURA_SEMI_BOLD } from '@monorepo/shared/src/themes/defaultTheme';
import useReports from 'state/hooks/use-reports';

import ProgressChart from './ProgressChart';
import ProgressCard from '../common/ProgressCard';
import { computePercent } from 'util/math-util';

const t = (key: string, options?) => translate('reports', key, options);

const OverallProgress: React.FC = () => {
  const classes = useStyles();

  const [{ charactersProgress, isLoading, selectedCharacter, filteredStudents }] = useReports();

  if (!selectedCharacter) return null;

  const progress = charactersProgress?.find(
    (character) => selectedCharacter.id === character.characterId
  );

  const bookReadPercentage = computePercent(
    progress?.bookReadStudentCount ?? 0,
    filteredStudents.length
  );

  const activityCompletedPercentage = computePercent(
    progress?.activityCompletedCount ?? 0,
    progress?.activityTotalCount ?? 0
  );

  return (
    <Box
      component={Paper}
      padding={6}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      gap={'1rem'}
    >
      <Box display="flex" flex-direction="column" gap={remCalc(58)}>
        <Box display="flex" flex-direction="column" alignItems="center">
          {isLoading ? (
            <Skeleton />
          ) : (
            <ProgressChart
              progress={bookReadPercentage}
              color={theme.palette.link.light}
              icon={faBookReader}
            />
          )}
          <Typography className={classes.chartTitle} style={{ maxWidth: remCalc(200) }}>
            {t('ofStudentsReadTheBook')}
          </Typography>
        </Box>
        <Box display="flex" flex-direction="column" alignItems="center">
          {isLoading ? (
            <Skeleton />
          ) : (
            <ProgressChart
              progress={activityCompletedPercentage}
              color={theme.palette.secondary.dark}
              icon={faShapes}
            />
          )}
          <Typography className={classes.chartTitle}>{t('activityCompletion')}</Typography>
        </Box>
        <Box alignItems="center">
          {isLoading ? (
            <Skeleton />
          ) : (
            <ProgressCard
              title={t('noOfActivitiesSkipped')}
              highlightedText={progress?.activitySkippedCount ?? 0}
              label={t('activities')}
              helpText={t('noOfActivitiesSkippedHelp')}
              noOutline
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  chartTitle: {
    fontSize: remCalc(18),
    lineHeight: remCalc(28),
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
  },
}));

export default OverallProgress;
